import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  RefreshCw, Send, ClipboardList, Calendar, DollarSign,
  AlertCircle, Search, ArrowRight, FileText, Briefcase,
} from 'lucide-react';
import { useToast, ToastContainer } from '../common/Toast';
import DataTable from '../common/DataTable';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import SourcingRfpService from '../../services/sourcingRFPService';

const RfpStatusBadge = ({ status }) => {
  const map = {
    PUBLISHED: { bg: '#d1fae5', color: '#059669', dot: '#10b981', label: 'Published' },
    OPEN:      { bg: '#dbeafe', color: '#1d4ed8', dot: '#3b82f6', label: 'Open' },
    DRAFT:     { bg: '#f3f4f6', color: '#6b7280', dot: '#9ca3af', label: 'Draft' },
    CLOSED:    { bg: '#fee2e2', color: '#dc2626', dot: '#ef4444', label: 'Closed' },
  };
  const s = map[status] || map.DRAFT;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 10px', borderRadius: 999, fontSize: 12,
      fontWeight: 600, background: s.bg, color: s.color,
      border: `1px solid ${s.color}22`,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.dot }} />
      {s.label}
    </span>
  );
};

const fmtDate = (val) => {
  if (!val) return '\u2014';
  try {
    return new Date(val).toLocaleDateString(undefined, {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return '\u2014'; }
};

const fmtBudget = (val) => {
  if (!val && val !== 0) return '\u2014';
  return `$${Number(val).toLocaleString()}`;
};

const truncate = (str, n = 100) =>
  str && str.length > n ? str.slice(0, n) + '\u2026' : (str || '\u2014');

const extractRfps = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const VendorRfpList = () => {
  const navigate = useNavigate();
  const { error: showError, toasts, removeToast } = useToast();
  const [rfps, setRfps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const showErrorRef = useRef(showError);
  useEffect(() => { showErrorRef.current = showError; }, [showError]);

  const loadRfpsRef = useRef(null);
  useEffect(() => {
    const loadRfps = async () => {
      setLoading(true);
      try {
        const res = await SourcingRfpService.getRfps();
        const all = extractRfps(res);
        const open = all.filter(r =>
          ['PUBLISHED', 'OPEN'].includes(r.status?.toUpperCase?.() ?? r.status)
        );
        setRfps(open);
      } catch (err) {
        console.error('Failed to load RFPs:', err);
        showErrorRef.current('Could not load available RFPs. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    loadRfpsRef.current = loadRfps;
    loadRfps();
  }, []);

  const filtered = rfps.filter(rfp => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    return (
      rfp.title?.toLowerCase().includes(q) ||
      rfp.rfpNumber?.toLowerCase().includes(q) ||
      rfp.scopeOfWork?.toLowerCase().includes(q)
    );
  });

  const columns = [
    {
      key: '__spacer__', label: '', width: '12px', sortable: false,
      render: () => <div style={{ width: 12 }} />,
    },
    {
      key: 'rfpNumber', label: 'RFP ID',
      render: (_, rfp) => (
        <span style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--gold-primary, #b78a00)', fontSize: 13 }}>
          {rfp.rfpNumber || rfp._id?.slice(-8).toUpperCase() || 'N/A'}
        </span>
      ),
    },
    {
      key: 'title', label: 'Project Name',
      render: (_, rfp) => (
        <div className="flex flex-col">
          <span style={{ fontWeight: 600, color: '#111827', fontSize: 14 }}>{rfp.title || 'Untitled RFP'}</span>
          {rfp.projectType && <span style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{rfp.projectType}</span>}
        </div>
      ),
    },
    {
      key: 'scopeOfWork', label: 'Description',
      render: (_, rfp) => (
        <span style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.4 }}>
          {truncate(rfp.scopeOfWork || rfp.scope || rfp.description)}
        </span>
      ),
    },
    {
      key: 'budget', label: 'Budget',
      render: (_, rfp) => (
        <div className="flex items-center gap-1" style={{ color: '#059669', fontWeight: 700 }}>
          <DollarSign size={14} /> {fmtBudget(rfp.budget)}
        </div>
      ),
    },
    {
      key: 'responseDeadline', label: 'Deadline',
      render: (_, rfp) => {
        const d = rfp.responseDeadline || rfp.deadline;
        const isExpiringSoon = d && (new Date(d) - new Date()) < 3 * 24 * 60 * 60 * 1000;
        return (
          <div className="flex items-center gap-1.5" style={{ color: isExpiringSoon ? '#dc2626' : '#374151', fontWeight: isExpiringSoon ? 600 : 400 }}>
            <Calendar size={13} style={{ flexShrink: 0 }} />
            {fmtDate(d)}
            {isExpiringSoon && <span style={{ fontSize: 10, background: '#fee2e2', color: '#dc2626', padding: '1px 5px', borderRadius: 4, fontWeight: 700 }}>SOON</span>}
          </div>
        );
      },
    },
    {
      key: 'status', label: 'Status',
      render: (_, rfp) => <RfpStatusBadge status={rfp.status} />,
    },
    {
      key: 'action', label: 'Action', width: '160px',
      render: (_, rfp) => (
        <button
          onClick={() => navigate(`/subcontracting/rfps/bid/${rfp._id}`)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '7px 14px', borderRadius: 8,
            background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
            color: '#fff', fontWeight: 600, fontSize: 13,
            border: 'none', cursor: 'pointer',
            boxShadow: '0 1px 4px rgba(37,99,235,0.3)',
            transition: 'all 0.15s ease', whiteSpace: 'nowrap',
          }}
          onMouseEnter={e => e.currentTarget.style.opacity = '.88'}
          onMouseLeave={e => e.currentTarget.style.opacity = '1'}
        >
          <Send size={14} /> Submit Bid
        </button>
      ),
    },
  ];

  const totalOpen   = rfps.length;
  const closingSoon = rfps.filter(r => {
    const d = r.responseDeadline || r.deadline;
    return d && (new Date(d) - new Date()) < 3 * 24 * 60 * 60 * 1000;
  }).length;
  const totalBudget = rfps.reduce((s, r) => s + (Number(r.budget) || 0), 0);

  const headerActions = [
    { icon: <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />, variant: 'outline', onClick: () => loadRfpsRef.current?.() },
  ];

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title="Available RFPs"
        description="Browse published sourcing events and submit your bids"
        actions={headerActions}
      />
      <div className="page-container">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-500 font-medium">Open RFPs</div>
              <div className="text-2xl font-bold text-gray-900">{totalOpen}</div>
            </div>
            <div className="bg-blue-50 text-blue-600 p-3 rounded-lg"><ClipboardList size={24} /></div>
          </div>
          <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-500 font-medium">Closing Soon</div>
              <div className="text-2xl font-bold text-red-500">{closingSoon}</div>
            </div>
            <div className="bg-red-50 text-red-500 p-3 rounded-lg"><AlertCircle size={24} /></div>
          </div>
          <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-500 font-medium">Total Budget Available</div>
              <div className="text-2xl font-bold text-green-600">
                {totalBudget > 0 ? `$${totalBudget.toLocaleString()}` : '\u2014'}
              </div>
            </div>
            <div className="bg-green-50 text-green-600 p-3 rounded-lg"><DollarSign size={24} /></div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6 shadow-sm flex items-center gap-3">
          <Search size={18} className="text-gray-400 flex-shrink-0" />
          <input type="text" placeholder="Search by RFP number, project name or scope\u2026"
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            style={{ flex: 1, border: 'none', outline: 'none', fontSize: 14, color: '#374151', background: 'transparent' }}
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')}
              style={{ fontSize: 12, color: '#6b7280', background: '#f3f4f6', border: 'none', borderRadius: 6, padding: '2px 8px', cursor: 'pointer' }}>
              Clear
            </button>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <DataTable data={filtered} columns={columns} loading={loading} showActions={false}
            emptyMessage={searchTerm ? `No RFPs match "${searchTerm}".` : 'No published RFPs available at this time.'}
          />
        </div>

        {!loading && rfps.length > 0 && (
          <div style={{ marginTop: 20, padding: '14px 20px', background: 'linear-gradient(135deg, #eff6ff, #dbeafe)', borderRadius: 12, border: '1px solid #bfdbfe', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <p style={{ fontWeight: 600, color: '#1e40af', margin: 0, fontSize: 14 }}>Already submitted a bid?</p>
              <p style={{ color: '#3b82f6', margin: '2px 0 0', fontSize: 13 }}>View and manage your submitted bids.</p>
            </div>
            <button onClick={() => navigate('/subcontracting/rfps/my-bids')}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, border: '1px solid #3b82f6', background: '#fff', color: '#2563eb', fontWeight: 600, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
              My Bids <ArrowRight size={14} />
            </button>
          </div>
        )}
      </div>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default VendorRfpList;
